import ArrowsIcon from 'assets/icons/arrows.svg?react';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import { Errors } from 'utils/formErrors';
import { useClickOutside } from 'hooks/useClickOutside';
import useErrorMessage from 'hooks/useErrorMessages';
import React, { useCallback, useEffect } from 'react';
import { GroupedOption, Option, Options } from 'types/SelectInputs';
import Dropdown from './Dropdown';
import styles from './SearchSelect.module.css';
import { isGroupedOptions, flattenOptions } from './utils';

interface SearchSelectInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    options: Options;
    errors?: Errors;
    icon?: React.ReactNode;
    selected?: Option;
    onSelected: (value: string) => void;
}

const emptyOption: Option = { value: '', label: '' };

function SearchSelectInput({
    label,
    errors,
    options,
    selected,
    onSelected,
    ...props
}: SearchSelectInputProps): JSX.Element {
    const [search, setSearch] = React.useState('');
    const [selectedOption, setSelected] = React.useState<Option | undefined>(selected || emptyOption);
    const [results, setResults] = React.useState<Options>(options);
    const [dropdownVisible, setDropdownVisible] = React.useState(false);
    const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const errorMessage = useErrorMessage(errors);

    React.useEffect(() => {
        // This allows parent to clear the selection
        if (!selected) setSelected(emptyOption);
    }, [selected]);

    function filterResults(options: Options, query: string): Options {
        if (isGroupedOptions(options[0])) {
            return (options as GroupedOption[])
                .map((optionOrGroup) => {
                    const groupe = optionOrGroup as GroupedOption;
                    const filteredOptions = groupe.options.filter((option) =>
                        option.label.toLowerCase().includes(query.toLowerCase())
                    );
                    return filteredOptions.length > 0 ? { ...groupe, options: filteredOptions } : null;
                })
                .filter((optionOrGroup) => optionOrGroup !== null) as Options;
        }

        return (options as Option[]).filter((option) => {
            return option.label.toLowerCase().includes(query.toLowerCase());
        });
    }

    const handleSelectOption = useCallback(
        (option: Option | undefined) => {
            setSelected(option);
            setSearch(option?.label || '');
            onSelected(option?.value || '');
            setDropdownVisible(false);
        },
        [onSelected]
    );

    useEffect(() => {
        const res = filterResults(options, search);
        setResults(res);
    }, [search, options]);

    useEffect(() => {
        if (dropdownVisible) {
            setSearch('');
            setHighlightedIndex(-1);
        } else {
            setSearch(selectedOption?.label || '');
        }
    }, [dropdownVisible, selectedOption]);

    const flatResults = React.useMemo(() => flattenOptions(results), [results]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (!dropdownVisible) {
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    setDropdownVisible(true);
                }
                return;
            }

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setHighlightedIndex((prev) => (prev < flatResults.length - 1 ? prev + 1 : prev));
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (highlightedIndex >= 0 && highlightedIndex < flatResults.length) {
                        handleSelectOption(flatResults[highlightedIndex]);
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    setDropdownVisible(false);
                    break;
            }
        },
        [dropdownVisible, flatResults, highlightedIndex, handleSelectOption]
    );

    const ref = useClickOutside(() => setDropdownVisible(false));

    return (
        <div className={['select-wrapper', errorMessage && 'error', props.disabled && 'disabled'].join(' ')}>
            {label !== undefined && (
                <div className="label-wrapper">
                    {props.icon && <div className="icon-wrapper">{props.icon}</div>}
                    <label className="select-label">{label}</label>
                </div>
            )}

            <div ref={ref} className={styles.searchSelectWrapper}>
                <div>
                    <input
                        value={search}
                        type="text"
                        onChange={(event) => setSearch(event.target.value)}
                        onFocus={() => setDropdownVisible(true)}
                        onKeyDown={handleKeyDown}
                        ref={inputRef}
                        {...props}
                    />
                    <button className={styles.arrowsButton} type="button" onClick={() => inputRef.current?.focus()}>
                        <ArrowsIcon />
                    </button>
                </div>

                {dropdownVisible && (
                    <Dropdown
                        options={results}
                        visible={dropdownVisible}
                        selectedOption={selectedOption}
                        handleSelectOption={handleSelectOption}
                        highlightedIndex={highlightedIndex}
                    />
                )}

                <input type="hidden" name={label} value={selectedOption?.value} />
            </div>

            {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
        </div>
    );
}

export default SearchSelectInput;
